import { createFileRoute, Link } from "@tanstack/react-router";
import { FileText, Printer, Share2, Phone, MessageCircle, MapPin, ArrowRight } from "lucide-react";
import { seoHead, SITE_ORIGIN } from "@/lib/seo";
import { CTABlock } from "@/components/CTABlock";

const RESOURCES = [
  {
    to: "/resources/mot-pass-checklist",
    title: "MOT Pass Preparation Checklist",
    blurb: "A printable 12-section check to spot common MOT issues before your test.",
  },
  {
    to: "/resources/pco-driver-vehicle-checklist",
    title: "PCO / Private-Hire Driver Vehicle Checklist",
    blurb: "Daily, weekly and pre-renewal checks for Uber, Bolt and private-hire drivers.",
  },
  {
    to: "/resources/used-car-viewing-checklist",
    title: "Used Car Viewing Checklist",
    blurb: "What to look for inside, outside, under the bonnet and on the test drive.",
  },
  {
    to: "/resources/dashboard-warning-light-guide",
    title: "Dashboard Warning Light Action Guide",
    blurb: "What each light may mean, when to stop, and when to call a garage.",
  },
] as const;

const INTERNAL_LINKS = [
  { to: "/mot", label: "MOT Testing" },
  { to: "/servicing", label: "Car Servicing" },
  { to: "/diagnostics", label: "Engine Diagnostics" },
  { to: "/pco-mot-east-london", label: "PCO MOT" },
  { to: "/value-for-money-garage-east-london", label: "Value-for-Money Garage" },
  { to: "/google-maps-garage-e14", label: "Find us on Google Maps" },
  { to: "/book", label: "Book a slot" },
  { to: "/contact", label: "Contact us" },
] as const;

export const Route = createFileRoute("/free-car-resources")({
  head: () =>
    seoHead({
      path: "/free-car-resources",
      title: "Free Car Checklists & MOT Resources | Woodseer Autos East London",
      description:
        "Free MOT, servicing, tyre, brake, warning light and used-car checklists from Woodseer Autos, an independent garage in East London.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Free Car Resources", path: "/free-car-resources" },
      ],
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Free car checklists & MOT resources",
          itemListElement: RESOURCES.map((r, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: r.title,
            url: `${SITE_ORIGIN}${r.to}`,
          })),
        },
      ],
    }),
  component: HubPage,
});

function HubPage() {
  return (
    <>
      <section className="bg-foreground text-background py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 md:px-6">
          <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-primary">
            Free resources
          </p>
          <h1 className="mt-3 text-3xl md:text-5xl font-extrabold tracking-tight">
            Free Car Checklists & MOT Resources
          </h1>
          <p className="mt-5 text-base md:text-lg text-background/70 leading-relaxed max-w-2xl">
            Practical, printable resources for East London drivers — free to use, share and link to.
            Written by an independent garage in Poplar, E14.
          </p>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="mx-auto max-w-5xl px-4 md:px-6 grid md:grid-cols-2 gap-5">
          {RESOURCES.map((r) => (
            <Link
              key={r.to}
              to={r.to}
              className="group block rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition-colors"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 text-primary">
                <FileText className="h-5 w-5" />
              </div>
              <h2 className="mt-4 text-lg font-bold text-foreground">{r.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{r.blurb}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:underline">
                Open resource <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="py-14 md:py-20 bg-secondary/30 border-y border-border">
        <div className="mx-auto max-w-4xl px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-foreground">
            Who can use these?
          </h2>
          <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-muted-foreground">
            <li>• Everyday drivers and family-car owners</li>
            <li>• PCO / private-hire drivers</li>
            <li>• Uber, Bolt and minicab drivers</li>
            <li>• Delivery drivers and couriers</li>
            <li>• Local businesses with vans</li>
            <li>• Driving instructors</li>
            <li>• Used-car buyers and sellers</li>
            <li>• Community websites and local blogs</li>
          </ul>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="mx-auto max-w-3xl px-4 md:px-6">
          <div className="flex items-center gap-2 text-primary">
            <Share2 className="h-5 w-5" />
            <p className="text-[11px] font-bold tracking-[0.25em] uppercase">Link to these resources</p>
          </div>
          <h2 className="mt-3 text-2xl md:text-3xl font-extrabold tracking-tight text-foreground">
            Free to share — no gates, no sign-ups
          </h2>
          <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed">
            These resources are free to share. If you run a local website, blog, driving school, community
            group or business, you're welcome to link to this page as a helpful resource for drivers.
          </p>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
            Suggested attribution: "Source: Woodseer Autos, independent garage and DVSA-authorised MOT
            station in Poplar, East London."
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            {INTERNAL_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="inline-flex items-center justify-between rounded-md border border-border bg-card px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-secondary transition-colors"
              >
                {l.label}
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABlock
        headline="Need help with a check?"
        subtext="Call is fastest in workshop hours. Honest advice, fair pricing, no work without your approval."
      />
    </>
  );
}
